import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Bug, ArrowLeft } from 'lucide-react';

// Keyframes for the bounce animation (similar to Tailwind's animate-bounce)
const bounce = keyframes`
  0%, 100% {
    transform: translateY(-25%);
    animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
  }
  50% {
    transform: translateY(0);
    animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
  }
`;

// Container with responsive layout
const Container = styled.div`
  min-height: 100vh;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(90deg, #00419F 0%, #1562B7 50%, #1E90FF 100%);
`;

// Wrapper for content with responsive max-width
const Wrapper = styled.div`
  width: 100%;
  max-width: 42rem;
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 1rem;
  }
`;

// Logo Section
const LogoSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
`;

const LogoImage = styled.img`
  height: 120px;
  width: auto;
  max-width: 100%;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 1024px) {
    height: 100px;
  }

  @media (max-width: 768px) {
    height: 80px;
  }
`;

// Card container with responsive padding
const Card = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.5rem;
  padding: 2rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

// Bug icon container with center alignment
const BugIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const BugIcon = styled(Bug)`
  width: 5rem;
  height: 5rem;
  color: #ffffff;
  animation: ${bounce} 1s infinite;
`;

// Title and description with responsive text alignment
const Title = styled.h1`
  font-size: 2.25rem;
  font-weight: bold;
  color: #ffffff;
  text-align: center;
  margin-bottom: 1rem;
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  margin-bottom: 2rem;
  max-width: 28rem;
  margin-left: auto;
  margin-right: auto;
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

// Grid for stats cards with responsive columns
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

// Stat card with responsive styles
const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #ffffff;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: bold;
`;

// Action Button with responsive alignment
const ActionButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  text-decoration: none;
  transition: all 200ms;
  margin-top: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;

// Footer with responsive text size
const Footer = styled.div`
  margin-top: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

function NotFoundPage() {
  return (
    <Container>
      <Wrapper>
        {/* Logo Section */}
        <LogoSection>
          <LogoImage src="/zeroday-ops.png" alt="ZeroDay Ops" />
        </LogoSection>

        {/* 404 Card */}
        <Card>
          <BugIconContainer>
            <BugIcon />
          </BugIconContainer>
          <Title>404 - Security Breach Detected</Title>
          <Description>
            The resource you're looking for seems to have escaped our security perimeter.
            Our VAPT scanner couldn't locate the target endpoint.
          </Description>

          {/* Stats Cards */}
          <StatsGrid>
            <StatCard>
              <StatValue>404</StatValue>
              <StatLabel>Error Code</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>0</StatValue>
              <StatLabel>Vulnerabilities</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>100%</StatValue>
              <StatLabel>Security Score</StatLabel>
            </StatCard>
          </StatsGrid>

          {/* Action Button */}
          <div style={{ textAlign: 'center' }}>
            <ActionButton href="https://vapt.zerodayops.com/">
              <ArrowLeft size={16} />
              Return to Dashboard
            </ActionButton>
          </div>
        </Card>

        {/* Footer */}
        <Footer>
          Need help? Contact our security team at support@zerodayops.com
        </Footer>
      </Wrapper>
    </Container>
  );
}

export default NotFoundPage;